// Time log Day view (sprint 3 §9.2; sprint 7 §7.2 original layout). The list is live-polled
// (useDayEntries carries LIVE_QUERY_OPTIONS) and the URL lives in the page.
import { addDays, type LocalDate } from '@shared/calendar';
import { Clock, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '@/components/EmptyState';
import { EntryCard } from '@/components/entries/EntryCard';
import { EntryFormDialog } from '@/components/entries/EntryFormDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDayEntries } from '@/hooks/use-entries';
import { formatDurationHm, formatLocalDate } from '@/lib/format';
import type { EntryListItem } from '@/types/entries';
import { LogNavRow } from './LogNavRow';

const LOADING_ROWS = 3;

type FormTarget = { mode: 'create' } | { mode: 'edit'; entry: EntryListItem } | null;

interface DayViewProps {
  date: LocalDate;
  today: LocalDate;
  onNavigate: (date: LocalDate) => void;
}

export function DayView({ date, today, onNavigate }: DayViewProps): JSX.Element {
  const [form, setForm] = useState<FormTarget>(null);
  const entries = useDayEntries(date);
  const items = entries.data ?? [];
  const total = items.reduce((acc, e) => acc + e.durationSeconds, 0);

  return (
    <section>
      <LogNavRow
        unit="day"
        heading={formatLocalDate(date)}
        onPrevious={() => onNavigate(addDays(date, -1))}
        onNext={() => onNavigate(addDays(date, 1))}
        onToday={() => onNavigate(today)}
        todayDisabled={date === today}
      />
      <div className="mb-4 flex items-center justify-between gap-3">
        {/* "today" only when viewing today, "total" otherwise (A-3). */}
        <p className="font-mono text-sm text-muted-foreground">
          <span aria-label="Day total">{formatDurationHm(total)}</span> {date === today ? 'today' : 'total'}
        </p>
        <Button onClick={() => setForm({ mode: 'create' })}>
          <Plus className="[stroke-width:2]" /> Add entry
        </Button>
      </div>

      {entries.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: LOADING_ROWS }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState variant="page" icon={Clock} title="No entries yet" hint="Add an entry or start the timer" />
      ) : (
        <div className="space-y-0.5">
          {items.map((entry) => (
            <EntryCard key={entry.id} entry={entry} onEdit={(e) => setForm({ mode: 'edit', entry: e })} />
          ))}
        </div>
      )}

      <EntryFormDialog
        open={form !== null}
        onOpenChange={(open) => {
          if (!open) setForm(null);
        }}
        mode={form?.mode ?? 'create'}
        entry={form?.mode === 'edit' ? form.entry : undefined}
        initialDate={date}
      />
    </section>
  );
}
